import React, { useState, useEffect, useCallback, useRef } from "react";
import { manifestService } from "_services";
import PropTypes from "prop-types";

// Constants
const MANIFEST_STATUS_PENDING = "pending";
const DEFAULT_PER_PAGE = 10;
const DEFAULT_PAGE = 1;

/**
 * Generates a label for a manifest item
 * Format: abbreviation-contract_number-incident_name
 */
const generateManifestLabel = (item) => {
  if (!item) return "";

  const parts = [
    item.abbreviation,
    item.contract_number,
    item.incident_name,
  ].filter(Boolean);

  return parts.join("-");
};

/**
 * AllManifest Component
 * Displays a dropdown of pending manifests for a given resource
 *
 * @param {number|string} resourceId - The resource identifier
 * @param {Function} onSelect - Callback when a manifest is selected
 * @param {Function} setIncident - Callback to set the incident name
 */
export function AllManifest({ resourceId, onSelect, setIncident }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedManifestId, setSelectedManifestId] = useState("");
  const [manifests, setManifests] = useState([]);

  // Use refs to store callbacks to prevent unnecessary re-renders
  const onSelectRef = useRef(onSelect);
  const setIncidentRef = useRef(setIncident);

  // Update refs when callbacks change
  useEffect(() => {
    onSelectRef.current = onSelect;
    setIncidentRef.current = setIncident;
  }, [onSelect, setIncident]);

  /**
   * Fetches pending manifests from the API
   */
  const fetchManifests = useCallback(async () => {
    if (!resourceId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const requestData = {
        resourceId: Number(resourceId),
        manifestAvailabilityStatus: MANIFEST_STATUS_PENDING,
        perPage: DEFAULT_PER_PAGE,
        page: DEFAULT_PAGE,
      };

      const response = await manifestService?.manifestListV1(requestData);
      const payload = response?.data?.data ?? [];

      // Filter to ensure only pending manifests (backend should filter, but double-check)
      const pendingManifests = payload.filter(
        (item) => item?.manifest_status === MANIFEST_STATUS_PENDING
      );

      setManifests(pendingManifests);

      // Auto-select the first manifest if available
      if (pendingManifests.length > 0) {
        const firstManifest = pendingManifests[0];
        const manifestId = String(firstManifest.manifest_id);
        setSelectedManifestId(manifestId);
        setIncidentRef.current?.(firstManifest.incident_name || "");
        onSelectRef.current?.(manifestId);
      } else {
        setSelectedManifestId("");
        setIncidentRef.current?.("");
        onSelectRef.current?.("");
      }
    } catch (err) {
      console.error("Error fetching manifests:", err);
      setError("Failed to load manifests. Please try again.");
      setManifests([]);
      setSelectedManifestId("");
      setIncidentRef.current?.("");
      onSelectRef.current?.("");
    } finally {
      setLoading(false);
    }
  }, [resourceId]);

  // Fetch manifests when resourceId changes
  useEffect(() => {
    fetchManifests();
  }, [fetchManifests]);

  /**
   * Handles manifest selection change
   */
  const handleManifestChange = useCallback(
    (e) => {
      const manifestId = e.target.value;
      setSelectedManifestId(manifestId);

      const selectedManifest = manifests.find(
        (item) => String(item.manifest_id) === manifestId
      );

      if (selectedManifest) {
        onSelectRef.current?.(manifestId);
        setIncidentRef.current?.(selectedManifest.incident_name || "");
      } else {
        onSelectRef.current?.("");
        setIncidentRef.current?.("");
      }
    },
    [manifests]
  );


  const hasNoManifests = !loading && !error && manifests.length === 0;
  const hasError = !loading && error;

  return (
    <div>
      <div className="form-group">
        <label htmlFor="manifest-select">Select Manifest *</label>

        {/* Error Message */}
        {hasError && (
          <p style={{ color: "red", marginBottom: "8px" }}>{error}</p>
        )}

        {/* No Manifests Message */}
        {hasNoManifests && (
          <p style={{ color: "red", marginBottom: "8px" }}>
            No pending manifests are currently available
          </p>
        )}

        <select
          required
          disabled={loading}
          className="form-control"
          id="manifest-select"
          value={selectedManifestId || ""}
          onChange={handleManifestChange}
          aria-label="Select a manifest"
          aria-required="true"
        >
          {!loading && (
            <option value="" disabled>
              --Select Manifest--
            </option>
          )}
          {manifests.map((item) => (
            <option key={item.manifest_id} value={item.manifest_id}>
              {generateManifestLabel(item)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

AllManifest.propTypes = {
  resourceId: PropTypes.oneOfType([PropTypes.number, PropTypes.string])
    .isRequired,
  onSelect: PropTypes.func,
  setIncident: PropTypes.func,
};

AllManifest.defaultProps = {
  onSelect: null,
  setIncident: null,
};

export default AllManifest;
